<template>
  <div class="page-content">
    <div class="headings">
      <h2 class="title title-primary">{{ location }}</h2>
      <span>|</span>
      <h2 class="title title-primary">
        {{ $t("lang.pos-entry.dashboard.page_title_line_1") }}
      </h2>
      <span>|</span>
      <h3 class="title title-primary">
        {{ $t("lang.pos-entry.detail.page_title_2") }}
      </h3>
    </div>
    <div class="page-header">
      <div class="boxes" v-if="!shouldLoading">
        <bx-box dark color="outline" class="col-6 col-md-auto p-1">
          <bx-value
            :label="`${$t('lang.pos-entry.detail.box_label_1')}`"
            :value="`Nº ${id}`"
            font-size="xs"
          />
        </bx-box>
        <bx-box dark color="outline" class="col-6 col-md-auto p-1">
          <bx-value
            :label="`${$t('lang.pos-entry.detail.box_label_2')}`"
            :value="sale.end_time"
            font-size="xs"
          />
        </bx-box>
      </div>
      <skeleton-boxes :boxes="2" v-else />
    </div>

    <div class="page-main-content">
      <div class="container-fluid">
        <div class="row">
          <div class="col-12">
            <bx-box
              :title="
                data.length + ' ' + $t('lang.inventory.stocks.table_title')
              "
              no-padding
              :loading="shouldLoading"
            >
              <bx-table
                :data="data"
                :columns="columns"
                paginated
                v-if="!shouldLoading"
                backendPagination
                :currentPage="page"
                :perPage="itemsPerPage"
                :total="ordersCount"
                @search="onSearch"
                highlight-danger="quantity"
                @update:currentPage="updatePage"
              >
                <template slot-scope="props">
                  <bx-table-column field="danger" centered>
                    <img
                      v-if="props.row.quantity === 0"
                      src="@/assets/i/triangle-outline.svg"
                      style="height: 20px; width: 20px;"
                    />
                  </bx-table-column>
                  <bx-table-column field="gtin" centered>
                    {{ props.row.gtin }}
                  </bx-table-column>
                  <bx-table-column centered>
                    {{ props.row.quantity }}
                  </bx-table-column>
                  <bx-table-column field="section">{{
                    props.row.section
                  }}</bx-table-column>
                  <bx-table-column field="description">{{
                    props.row.description
                  }}</bx-table-column>
                  <bx-table-column centered>{{
                    props.row.cloth_type
                  }}</bx-table-column>
                  <bx-table-column centered>{{
                    props.row.model
                  }}</bx-table-column>
                  <bx-table-column centered>{{
                    props.row.color
                  }}</bx-table-column>
                  <bx-table-column centered>{{
                    props.row.size
                  }}</bx-table-column>
                </template>
              </bx-table>
            </bx-box>
          </div>
        </div>
        <v-row class="d-flex d-sm-none">
          <div
            v-infinite-scroll="loadMore"
            infinite-scroll-disabled="busy"
            infinite-scroll-distance="20"
            style="width: 100%"
          >
            <v-list width="100%">
              <v-list-item
                two-line
                v-for="(item, index) in dataList"
                :key="index"
                class="green lighten-5 ma-2 rounded-lg"
                ripple
              >
                <v-list-item-content>
                  <v-list-item-title>
                    <div>
                      {{ item.gtin }}
                    </div>
                  </v-list-item-title>
                  <v-list-item-subtitle>{{
                    item.section
                  }}</v-list-item-subtitle>
                  <v-list-item-subtitle class="text-caption">{{
                    item.cloth_type
                  }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-list-item-subtitle class="text-caption">
                    {{ item.quantity_real + "/" + item.quantity }}
                  </v-list-item-subtitle>
                  <v-list-item-subtitle class="text-caption">
                    {{ item.color }}
                  </v-list-item-subtitle>
                  <v-list-item-subtitle class="text-caption">
                    {{ item.model }}
                  </v-list-item-subtitle>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </div>
        </v-row>
        <skeleton-table :columns="6" v-if="shouldLoading" />
      </div>
    </div>
  </div>
</template>

<script>
import common from "@/utils/common";

export default {
  name: "inbound-dashboard",
  props: ["id"],
  mixins: [common],
  data() {
    return {
      shouldLoading: true,
      reloadInterval: null,
      ordersCount: "",
      columns: [],
      data: [],
      dataList: [],
      key: "inventory_stocks",
      filters: [],
      pending: 12,
      completed: 34,
      total: 46,
      page: 1,
      itemsPerPage: process.env.VUE_APP_ITEMS_PER_PAGE,
      sale: {}
    };
  },
  computed: {
    location() {
      return localStorage.getItem("location");
    }
  },
  methods: {
    loadMore() {
      if (
        !this.shouldLoading &&
        this.dataList.length &&
        this.dataList.length < this.ordersCount &&
        this.isMobile
      ) {
        this.page++;
        this.getData();
      }
    },
    onSearch(field, value) {
      this.$session.set(this.key + field, value);
      this.filters[field] = value;
      this.getData();
    },
    updatePage(p) {
      this.page = p;
      this.getData(this.locationId);
    },
    async getData() {
      try {
        if (this.isMobile) {
          this.shouldLoading = true;
        }
        let location = localStorage.getItem("location");
        let locationId = localStorage.getItem("location_id");
        if (!location || !locationId) {
          try {
            let firstLocation = await this.$api.location.search("", {
              limit: 1,
              offset: 0,
              orderBy: "id"
            });
            location = firstLocation.results[0].description;
            locationId = firstLocation.results[0].id;
          } catch (error) {
            console.log(error);
          }
        }
        this.sale = await this.$api.location.returnDetail(this.id);

        // Load inventories
        const inventories = await this.$api.location.salesGtins({
          offset: (this.page - 1) * this.itemsPerPage,
          limit: this.itemsPerPage,
          item__itemseen__tagReportData__reading__reading_set: this.id
        });
        this.ordersCount = inventories.count;
        this.data = inventories.results.map(item => {
          return {
            id: item.id,
            gtin: item.gtin,
            quantity: item.quantity,
            description: item.description,
            section: item.section,
            cloth_type: item.cloth_type,
            size: item.size,
            color: item.color,
            model: item.model
          };
        });

        if (this.isMobile) {
          this.dataList = this.dataList.concat(this.data);
        }
        this.columns = [
          {
            field: "danger",
            width: "10",
            centered: true
          },
          {
            field: "gtin",
            label: this.$t("lang.inventory.stocks.table_field_1"),
            width: "120",
            centered: true,
            filter: {
              placeholder: this.$t(
                "lang.inbound.dashboard.table_field_placeholder_4"
              ),
              value: this.filters.gtin
            }
          },
          {
            field: "quantity",
            label: this.$t("lang.inventory.stocks.table_field_2"),
            width: "120",
            centered: true
          },
          {
            field: "section",
            label: this.$t("lang.inventory.stocks.table_field_3")
          },
          {
            field: "description",
            label: this.$t("lang.pos-exit.detail.table_field_description"),
            width: "120",
            centered: true
          },
          {
            field: "cloth_type",
            label: this.$t("lang.inventory.stocks.table_field_4"),
            centered: true
          },
          {
            field: "model",
            label: this.$t("lang.inventory.stocks.table_field_5"),
            centered: true
          },
          {
            field: "color",
            label: this.$t("lang.inventory.stocks.table_field_6"),
            centered: true
          },

          {
            field: "size",
            label: this.$t("lang.inventory.stocks.table_field_7"),
            centered: true
          }
        ];
      } catch (error) {
        // console.log(error)
      } finally {
        this.shouldLoading = false;
      }
    }
  },
  mounted() {
    if (!this.isMobile) {
      this.reloadInterval = setInterval(
        this.getData,
        process.env.VUE_APP_RELOAD_TIME
      );
    }
    this.getData();
    this.filters.gtin = this.$session.get(this.key + "gtin");
  },
  beforeDestroy() {
    clearInterval(this.reloadInterval);
  }
};
</script>
